import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedQuery } from '../../../../../service'
import SydLabel, { hookFormFieldErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import SydButton from '../../../../commonDesign/Button'
import SydCompositeDateTimePicker from '../../../../commonDesign/SydCompositeDateTimePicker'
import SydDurationPicker from '../../../../commonDesign/SydDurationPicker'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import WYSIWYGEditor from '../../../WYSIWYGEditor'
import {
  membersToOptions, renderContactOption, editorBorderStyle, PRIORITY_OPTIONS,
  RECURRENCE_OPTIONS_WITH_CUSTOM, CUSTOM_VALUE, defaultRecurrenceState, serializeRecurrence
} from '../shared/redtailUIHelpers'
import RecurrenceEditor from '../shared/RecurrenceEditor'
import { LOCAL_TZ_ABBR } from './meetingHelpers'

const DEBOUNCE_MS = 350

function MeetingForm ({
  defaultValues,
  config,
  members,
  categoryOptions,
  onSubmit,
  onCancel,
  submitLabel = 'Create Meeting',
  cancelVariant = 'outline',
  bladeOpen,
  onBladeChange
}) {
  const { clientId } = useAppContext()
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [searchOptions, setSearchOptions] = useState([])
  const [contactLoading, setContactLoading] = useState(false)
  // TODO: Consider integrating recurrenceState into react-hook-form (useController or
  // register with custom field) so form reset, dirty-tracking, and validation are unified.
  // Currently state cleanup relies on component unmount via SydModal.
  const [recurrenceState, setRecurrenceState] = useState(() => defaultRecurrenceState('daily'))
  const debounceRef = useRef(null)
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  const isUserScoped = config?.scope === 'user'
  const familyOptions = useMemo(() => membersToOptions(members), [members])
  const contactOptions = isUserScoped ? searchOptions : familyOptions

  const { control, handleSubmit, watch, setValue, getValues } = useForm({
    defaultValues
  })

  const allDay = watch('all_day')
  const startDate = watch('start_date')
  const duration = watch('duration')

  const handleRepeatsChange = useCallback((val) => {
    if (val === CUSTOM_VALUE) {
      onBladeChange(true)
      setValue('repeats', CUSTOM_VALUE)
    } else {
      onBladeChange(false)
      setValue('repeats', val)
    }
  }, [setValue, onBladeChange])

  const computedEnd = useMemo(() => {
    if (allDay || !startDate) return null
    const end = dayjs(startDate).add(duration, 'minute')
    if (!end.isValid()) return null
    const tzSuffix = LOCAL_TZ_ABBR ? ` ${LOCAL_TZ_ABBR}` : ''
    return `${end.format('MMM D, h:mm A')}${tzSuffix}`
  }, [allDay, startDate, duration])

  const handleStartBlur = useCallback(() => {
    const start = getValues('start_date')
    if (start && !getValues('end_date')) {
      setValue('end_date', dayjs(start).add(1, 'day').format('YYYY-MM-DD'), { shouldValidate: true })
    }
  }, [getValues, setValue])

  const searchContacts = useCallback((term) => {
    if (!isUserScoped) return
    if (!term || term.length < 2) {
      setSearchOptions([])
      return
    }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setContactLoading(true)
      try {
        const { data } = await postNamedQuery('coreData', 'execute-redtail-domain-query', {
          query: 'searchContactsBasic',
          autoResolveConnection: true,
          clientId,
          request: { params: { name: term } }
        })
        const contacts = data?.contacts ?? []
        setSearchOptions(contacts.map(c => ({
          label: c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || `Contact ${c.id}`,
          value: c.id
        })))
      } catch {
        setSearchOptions([])
      } finally {
        setContactLoading(false)
      }
    }, DEBOUNCE_MS)
  }, [clientId, isUserScoped])

  const handleFormSubmit = useCallback(async (values) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const startDayjs = values.start_date ? dayjs(values.start_date) : null
      const recurrenceFields = values.repeats === CUSTOM_VALUE
        ? serializeRecurrence(recurrenceState)
        : { repeats: values.repeats || 'never' }
      const body = {
        subject: values.subject,
        ...recurrenceFields,
        all_day: values.all_day || false,
        location: values.location || undefined,
        description: values.description || undefined,
        priority: values.priority || undefined,
        category_id: values.category_id || undefined
      }

      if (values.all_day) {
        body.start_date = startDayjs?.toISOString()
        body.end_date = values.end_date ? dayjs(values.end_date).toISOString() : undefined
      } else {
        body.start_date = startDayjs?.toISOString()
        body.end_date = startDayjs?.add(values.duration, 'minute').toISOString()
      }

      if (values.contacts?.length) {
        body.linked_contacts = values.contacts.map(c => ({ contact_id: c.value }))
      }

      await onSubmit(body)
    } catch (err) {
      console.error('Failed to save meeting', err)
      setSubmitError(err?.message || 'Failed to save meeting')
    } finally {
      setSubmitting(false)
    }
  }, [recurrenceState, onSubmit])

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      <div style={{ display: 'flex', gap: bladeOpen ? '20px' : '0px' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Controller
            name='subject'
            control={control}
            rules={{ required: 'Subject is required' }}
            render={({ field, fieldState }) => (
              <SydLabel
                required
                label='Subject'
                error={hookFormFieldErrorAdapter(fieldState)}
              >
                <SydInput {...field} placeholder='Meeting subject' />
              </SydLabel>
            )}
          />

          {allDay ? (
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <Controller
                  name='start_date'
                  control={control}
                  rules={{ required: 'Start date is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel required label='Start' error={hookFormFieldErrorAdapter(fieldState)}>
                      <SydInput
                        {...field}
                        onBlur={(e) => { field.onBlur(e); handleStartBlur() }}
                        type='date'
                      />
                    </SydLabel>
                  )}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Controller
                  name='end_date'
                  control={control}
                  rules={{ required: 'End date is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel required label='End' error={hookFormFieldErrorAdapter(fieldState)}>
                      <SydInput {...field} type='date' />
                    </SydLabel>
                  )}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Controller
                  name='start_date'
                  control={control}
                  rules={{ required: 'Start date is required' }}
                  render={({ field, fieldState }) => (
                    <SydLabel required label='Start' error={hookFormFieldErrorAdapter(fieldState)}>
                      <SydCompositeDateTimePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        timezone={LOCAL_TZ_ABBR}
                      />
                    </SydLabel>
                  )}
                />
              </div>
              <Controller
                name='duration'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Duration'>
                    <SydDurationPicker
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </SydLabel>
                )}
              />
              <div>
                <SydLabel label='Ends'>
                  <SydInput
                    readOnly
                    minWidth={0}
                    tabIndex={-1}
                    value={computedEnd || ''}
                    placeholder=''
                    style={{ cursor: 'default', color: 'var(--color-neutral-700)' }}
                  />
                </SydLabel>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Controller
              name='all_day'
              control={control}
              render={({ field }) => (
                <SydCheckbox
                  label='All-day event'
                  size='small'
                  value={field.value}
                  onChange={(_e, checked) => field.onChange(checked)}
                />
              )}
            />
            <Controller
              name='repeats'
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-color-subtle)', whiteSpace: 'nowrap' }}>Repeats</span>
                  <SydSelect
                    minWidth={120}
                    options={RECURRENCE_OPTIONS_WITH_CUSTOM}
                    value={field.value}
                    onChange={(e) => handleRepeatsChange(e.target.value)}
                  />
                </div>
              )}
            />
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <Controller
                name='contacts'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Contact(s)'>
                    <SydAutocomplete
                      multiple
                      chipVariant='neutral'
                      placeholder={isUserScoped ? 'Search contacts...' : 'Select family contacts...'}
                      options={contactOptions}
                      isLoading={contactLoading}
                      value={field.value}
                      onChange={(_e, val) => field.onChange(val)}
                      onInputChange={isUserScoped ? (_e, val) => searchContacts(val) : undefined}
                      renderOption={renderContactOption}
                    />
                  </SydLabel>
                )}
              />
            </div>
            <div style={{ width: '100px' }}>
              <Controller
                name='priority'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Priority'>
                    <SydSelect
                      minWidth={0}
                      options={PRIORITY_OPTIONS}
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </SydLabel>
                )}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <Controller
                name='location'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Location'>
                    <SydInput {...field} placeholder='Meeting location' />
                  </SydLabel>
                )}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Controller
                name='category_id'
                control={control}
                render={({ field }) => (
                  <SydLabel label='Category'>
                    <SydSelect
                      minWidth={0}
                      options={[{ label: '(None)', value: '' }, ...categoryOptions]}
                      value={field.value}
                      onChange={(e) => {
                        const val = e.target.value
                        field.onChange(val === '' ? '' : Number(val))
                      }}
                    />
                  </SydLabel>
                )}
              />
            </div>
          </div>

          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <SydLabel label='Description'>
                <div style={editorBorderStyle}>
                  <WYSIWYGEditor
                    defaultValue={field.value}
                    onChange={(val) => field.onChange(val)}
                    placeholder='Meeting description'
                    isMarkdown={false}
                    enableAutofocus={false}
                  />
                </div>
              </SydLabel>
            )}
          />

          {submitError ? (
            <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}>
              {submitError}
            </div>
          ) : null}
        </div>

        <RecurrenceEditor
          open={bladeOpen}
          value={recurrenceState}
          onChange={setRecurrenceState}
        />
      </div>

      <SydModalActions>
        <SydButton
          variant={cancelVariant}
          size='sm'
          onClick={onCancel}
          type='button'
        >
          Cancel
        </SydButton>
        <SydButton
          variant='primary'
          size='sm'
          type='submit'
          processing={submitting}
        >
          {submitLabel}
        </SydButton>
      </SydModalActions>
    </form>
  )
}

MeetingForm.propTypes = {
  defaultValues: PropTypes.object.isRequired,
  config: PropTypes.object,
  members: PropTypes.array,
  categoryOptions: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitLabel: PropTypes.string,
  cancelVariant: PropTypes.string,
  bladeOpen: PropTypes.bool,
  onBladeChange: PropTypes.func
}

export default MeetingForm
